import { useCallback, useEffect, useRef, useState } from 'react';

export function useDropdownGroup<K extends string>(keys: readonly K[]) {
  const [openKey, setOpenKey] = useState<K | null>(null);
  const refsMap = useRef<Record<K, HTMLDivElement | null>>(
    Object.fromEntries(keys.map((k) => [k, null])) as Record<K, HTMLDivElement | null>
  );
  const keysRef = useRef(keys);
  keysRef.current = keys;

  for (const key of keys) {
    if (!(key in refsMap.current)) {
      refsMap.current[key] = null;
    }
  }

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      const clickedInside = keysRef.current.some((k) => refsMap.current[k]?.contains(target));
      if (!clickedInside) setOpenKey(null);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggle = useCallback((key: K) => setOpenKey((prev) => (prev === key ? null : key)), []);

  const closeAll = useCallback(() => setOpenKey(null), []);

  const setRef = useCallback(
    (key: K) => (el: HTMLDivElement | null) => {
      refsMap.current[key] = el;
    },
    []
  );

  const isOpen = useCallback((key: K) => openKey === key, [openKey]);

  return { toggle, closeAll, setRef, isOpen };
}
